import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import ConfirmDestructiveDialog from '@/components/ConfirmDestructiveDialog';
import { InlineToggle } from '@/components/ui';
import { formatApiError } from '@/utils/formatApiError';

export type JobCleanupMode = 'completed' | 'failed' | 'selected';

type Props = {
  mode: JobCleanupMode;
  jobIds?: string[];
  olderThanDays?: number;
  onClose: () => void;
  onDone: (message: string) => void;
};

const MODE_STATUSES: Record<Exclude<JobCleanupMode, 'selected'>, string[]> = {
  completed: ['completed', 'cancelled'],
  failed: ['failed', 'partial_failed'],
};

const MODE_LABELS: Record<JobCleanupMode, string> = {
  completed: 'completed and cancelled',
  failed: 'failed and partially failed',
  selected: 'selected terminal',
};

type PreviewState = {
  eligibleCount: number;
  canClear: boolean;
  reason?: string | null;
};

export default function CleanupJobsConfirmModal({
  mode,
  jobIds,
  olderThanDays,
  onClose,
  onDone,
}: Props) {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [alsoClearActivity, setAlsoClearActivity] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError('');
    const previewPayload =
      mode === 'selected'
        ? { job_ids: jobIds ?? [] }
        : { statuses: MODE_STATUSES[mode], older_than_days: olderThanDays };
    try {
      const result = await api.jobs.cleanupPreview(previewPayload);
      const eligibleCount = result.eligibleCount ?? result.matched ?? 0;
      setPreview({
        eligibleCount,
        canClear: result.canClear ?? eligibleCount > 0,
        reason: result.reason,
      });
    } catch (e) {
      setError(formatApiError(e, 'Failed to load preview'));
    } finally {
      setLoadingPreview(false);
    }
  }, [mode, jobIds, olderThanDays]);

  const resetModalState = useCallback(() => {
    setPreview(null);
    setAlsoClearActivity(false);
    setLoadingPreview(true);
    setSubmitting(false);
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [onClose, resetModalState]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  async function confirm() {
    if (!preview?.canClear) return;
    setSubmitting(true);
    setError('');
    try {
      const cleanupPayload =
        mode === 'selected'
          ? { job_ids: jobIds ?? [], also_clear_activity: alsoClearActivity }
          : {
              statuses: MODE_STATUSES[mode],
              older_than_days: olderThanDays,
              also_clear_activity: alsoClearActivity,
            };
      const result = await api.jobs.cleanup(cleanupPayload);
      const deleted = result.deletedJobs ?? result.deleted ?? 0;
      const jobItems = result.deletedJobItems ?? 0;
      const related = result.deletedRelatedRows ?? result.activity_deleted ?? 0;
      const failed = result.failed ?? 0;
      const matchedCount = result.matched ?? preview.eligibleCount;
      let message: string;
      if (failed > 0) {
        message = `Removed ${deleted} of ${matchedCount} job(s); ${failed} could not be removed`;
      } else if (related > 0) {
        message = `Removed ${deleted} job(s), ${jobItems} job item(s), and ${related} related activity record(s)`;
      } else if (deleted === 0) {
        message = `No ${MODE_LABELS[mode]} jobs to remove`;
      } else {
        message = `Removed ${deleted} job(s) and ${jobItems} job item(s)`;
      }
      try {
        await Promise.resolve(onDone(message));
      } catch {
        // Parent refresh/toast is best-effort after successful cleanup.
      }
      handleClose();
    } catch (e) {
      setError(formatApiError(e, 'Cleanup failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const title =
    mode === 'selected'
      ? 'Clear selected jobs?'
      : `Clear ${mode === 'completed' ? 'completed' : 'failed'} jobs?`;

  const eligibleCount = preview?.eligibleCount ?? 0;
  const canClear = preview?.canClear ?? false;

  return (
    <ConfirmDestructiveDialog
      title={title}
      onClose={handleClose}
      closeOnBackdrop={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={submitting || loadingPreview || !canClear}
            onClick={() => void confirm()}
          >
            {submitting ? 'Removing…' : 'Clear jobs'}
          </button>
        </>
      }
    >
      {error ? <div className="alert alert-error">{error}</div> : null}

      <p className="modal-lead">
        {loadingPreview ? (
          'Counting matching jobs…'
        ) : !canClear ? (
          <>
            No {MODE_LABELS[mode]} jobs are eligible for removal right now.
            {preview?.reason === 'no_eligible_jobs' ? ' Queued and processing jobs are protected.' : null}
          </>
        ) : (
          <>
            This will permanently remove <strong>{eligibleCount}</strong> {MODE_LABELS[mode]} job(s) and their
            job items (up to 500 per run).
          </>
        )}
      </p>

      {mode === 'selected' && eligibleCount === 0 && !loadingPreview ? (
        <p className="modal-hint">
          Only terminal jobs (completed, failed, cancelled) can be cleared. Queued and processing jobs are
          protected.
        </p>
      ) : null}

      <InlineToggle
        checked={alsoClearActivity}
        onChange={setAlsoClearActivity}
        disabled={submitting}
        label="Also clear activity logs"
        className="modal-checkbox-field"
      />

      <p className="modal-hint muted">
        Removes deliveries, pipeline executions, and fetch runs. Queued and processing jobs are never removed.
        Discovered links are kept.
      </p>
    </ConfirmDestructiveDialog>
  );
}
